import { AttackPattern, PatternState } from '../AttackPattern.js';
import { ARENA } from '../../../../config/constants.js';

/**
 * Hushfall (The Great Hush, Act II) — the signature that fights you with less.
 * It scatters growing zones of silence across the arena; standing in one makes
 * the player's weapons (and aura) go dumb — no damage output, though movement
 * and dodging are untouched. It cannot hurt you. It can only make you unable to
 * answer while the boss's normal patterns keep firing, so the fight becomes
 * "keep dealing damage from the shrinking floor where the song still plays."
 *
 * The zones don't damage and don't spawn bullets: the whole mechanic is the
 * per-frame `player.muted` flag this pattern sets from zone membership, read by
 * CombatSystem.update to suppress the fire loop. Zones seed away from arena
 * center (so there's somewhere to fight from at the start) and grow across the
 * active window, squeezing that safe space. `player.muted` is reset here every
 * active tick (true/false), cleared in onEnd, and defensively cleared by
 * SpawningSystem on desperation — so it can never stick.
 */
export class Hushfall extends AttackPattern {
  constructor({ minPhase = 1, config }) {
    super({ name: 'Hushfall', telegraphDuration: 1.0, activeDuration: 5.0, minPhase });
    this.config = config;
    this._zones = [];
  }

  onBegin(_boss, player) {
    if (player) player.muted = false;
    this._zones = [];
    this._totalHush = 0;
    this._totalHushCooldown = 2;
    const cx = ARENA.x + ARENA.width / 2;
    const cy = ARENA.y + ARENA.height / 2;
    const { zoneCount, zoneRadius } = this.config;
    const base = Math.random() * Math.PI * 2;
    for (let i = 0; i < zoneCount; i++) {
      const angle = base + (i / zoneCount) * Math.PI * 2;
      const dist = 150 + Math.random() * 40;
      const x = Math.min(ARENA.x + ARENA.width - 30, Math.max(ARENA.x + 30, cx + Math.cos(angle) * dist));
      const y = Math.min(ARENA.y + ARENA.height - 30, Math.max(ARENA.y + 30, cy + Math.sin(angle) * dist));
      this._zones.push({ x, y, radius: zoneRadius });
    }
  }

  onActiveTick(dt, boss, player, _pool) {
    const totalPhase = this.config.totalHushPhase ?? Infinity;
    if (boss.phase >= totalPhase) {
      this._totalHush = Math.max(0, this._totalHush - dt);
      this._totalHushCooldown = Math.max(0, this._totalHushCooldown - dt);
      if (this._totalHush <= 0 && this._totalHushCooldown <= 0) {
        this._totalHush = this.config.totalHushDuration ?? 1.2;
        this._totalHushCooldown = 3;
      }
      if (this._totalHush > 0) {
        player.muted = true;
        return;
      }
    }
    let inside = false;
    for (const zone of this._zones) {
      zone.radius += this.config.growthPerSec * dt;
      if (this.config.driftSpeed) {
        zone.x += (player.x - zone.x) * Math.min(1, this.config.driftSpeed * dt / Math.max(1, zone.radius));
        zone.y += (player.y - zone.y) * Math.min(1, this.config.driftSpeed * dt / Math.max(1, zone.radius));
      }
      if (Math.hypot(player.x - zone.x, player.y - zone.y) < zone.radius) inside = true;
    }
    player.muted = inside;
  }

  onEnd(_boss, player) {
    if (player) player.muted = false;
  }

  getVisual() {
    if (this.state === PatternState.TELEGRAPH || this.state === PatternState.ACTIVE) {
      return { kind: 'hushZones', zones: this._zones, imminent: this.state === PatternState.ACTIVE, totalHush: this._totalHush > 0 };
    }
    return null;
  }
}
